// 마진 시뮬레이터 — 데이터 모델·계산·영속화 (unified-trade-flow FR-2).
// ops-britzmedi /tools/margin 의 model.ts 를 무손실 이식한 것. 계산 수식은
// TORR_RF_인도_마진계산기_v1.1_260707.xlsx 를 일반화한 원본 그대로 (NG2 — 재설계 금지):
//   수수료/대 = 판매가×수수료율(총판매액 기준) + MAX(0, 판매가−인센티브 플로어)
//   회사 순이익/대 = (판매가 − 수수료)×환율 − 제조원가
// 저장: Supabase app_state (key='margin-desk') + localStorage 캐시(오프라인 폴백).
// ※ 원본과의 차이는 이 저장 계층뿐이다. 수식 변경 시 model.test.ts 가 깨진다.

import { appStateRepo } from "@/lib/db";

export type ProductId = "torr-rf" | "newchae" | "luminowave-kc" | "luminowave-md";

export const PRODUCTS: { id: ProductId; name: string; short: string; note?: string }[] = [
  { id: "torr-rf", name: "TORR RF", short: "TORR" },
  { id: "newchae", name: "NEW CHAE", short: "NC", note: "유통 중" },
  { id: "luminowave-kc", name: "LUMINO WAVE (KC)", short: "LW·KC", note: "출시 예정" },
  { id: "luminowave-md", name: "LUMINO WAVE (의료기기)", short: "LW·MD", note: "출시 예정" },
];

/** 가격 구간 레퍼런스 행 — 범위(fromQty~toQty)를 채우면 "기준안"에 자동 연동된다. */
export type Tier = { label: string; price: number; fromQty?: number | null; toQty?: number | null };
export type Scenario = { qty: number; price: number };

/**
 * 가격안(Offer) — 국가 탭 안에서 A/B/C… "딜 조건 패키지"를 통째로 비교하는 단위.
 * 실제 딜은 MOQ 구간 계단(20대@$14k + 30대@$12k + …)이 함께 움직이므로,
 * 안 하나가 구간(tier) 여러 개 + 자유 비용 명목(costs)을 품는다.
 */
export type OfferTier = {
  fromQty: number; // 구간 시작 수량 (이상)
  toQty: number | null; // 구간 끝 수량 (이하). null = 상한 없음("이상")
  price: number; // 이 구간에 걸리면 전량 적용되는 단가 USD/대
  /** @deprecated 무상은 구간이 아니라 안/계약 레벨 조건(freeFixed·freePerN)으로 이동 (2026-07-14) */
  freeUnits: number;
};

/** 무상 제공 조건 — 구간과 독립적으로 딜 전체에 걸린다 (예: 누적 30대당 1대). */
export type FreeRule = {
  freeFixed: number; // 딜에 고정으로 끼워주는 무상 대수
  freePerN: number | null; // 누적 기준 수량 (예: 30). null = 누적 규칙 없음
  freePerUnits: number; // 기준 도달마다 무상 대수 (예: 1)
};
export const emptyFree = (): FreeRule => ({ freeFixed: 0, freePerN: null, freePerUnits: 1 });

/** 무상 발생량 = 고정 + floor(유상 누적 / N) × M. */
export function freeEarned(rule: FreeRule, paidQty: number): number {
  const per =
    rule.freePerN && rule.freePerN > 0
      ? Math.floor((paidQty || 0) / rule.freePerN) * (rule.freePerUnits || 0)
      : 0;
  return (rule.freeFixed || 0) + per;
}

/** 비용 명목의 산정 기준 — 고정액 또는 비율로 자유 입력. */
export type CostBase = "fixed_krw" | "per_unit_krw" | "pct_revenue" | "pct_margin";
export const COST_BASES: { id: CostBase; label: string; unit: string }[] = [
  { id: "fixed_krw", label: "고정 금액", unit: "KRW" },
  { id: "per_unit_krw", label: "대당 금액", unit: "KRW/대" },
  { id: "pct_revenue", label: "매출의 %", unit: "%" },
  { id: "pct_margin", label: "총마진의 %", unit: "%" }, // 총마진 = 매출 − 제조원가
];

export type CostItem = {
  name: string; // 명목 (운임, 설치비, 마케팅 지원 …)
  base: CostBase;
  value: number; // fixed/per_unit → KRW, pct_* → % (예: 3 = 3%)
};

export type Offer = {
  label: string;
  /** true = "기준안": 구간표를 이 안이 직접 갖지 않고 국가의 가격 구간 레퍼런스(범위 입력된 행)와 실시간 연동 */
  linked?: boolean;
  tiers: OfferTier[]; // MOQ 구간표 (수량 범위 → 단가). linked=true면 무시되고 레퍼런스에서 파생
  orderQty: number; // 예상 주문 수량 — 이 수량으로 손익 산출
  free: FreeRule; // 무상 제공 조건 (구간과 독립 — 고정 + 누적 N대당 M대)
  costs: CostItem[]; // 자유 추가 비용 명목
  rateOverride: number | null; // 수수료율 특례 (0~1), null이면 국가 기본
  note: string;
};

/** 레퍼런스 구간 중 범위가 입력된 행 → 기준안용 구간표로 파생. */
export function refTierRanges(c: Pick<CountrySim, "tiers">): OfferTier[] {
  return c.tiers
    .filter((t) => typeof t.fromQty === "number" && Number.isFinite(t.fromQty))
    .map((t) => ({ fromQty: t.fromQty as number, toQty: t.toQty ?? null, price: t.price, freeUnits: 0 }));
}

/** 안의 실제 적용 구간표 — 기준안(linked)은 레퍼런스 파생, 일반 안은 자체 구간표. */
export function effectiveTiers(o: Offer, c: Pick<CountrySim, "tiers">): OfferTier[] {
  return o.linked ? refTierRanges(c) : o.tiers;
}

/** 주문 수량이 걸리는 구간 찾기 — 범위 포함 우선, 없으면 이하 구간 중 최고 시작점, 그마저 없으면 첫 구간. */
export function applicableTier(tiers: OfferTier[], orderQty: number): { tier: OfferTier | null; index: number } {
  const sorted = tiers.map((t, index) => ({ t, index })).sort((a, b) => (a.t.fromQty || 0) - (b.t.fromQty || 0));
  const q = orderQty || 0;
  let hit = sorted.find(({ t }) => q >= (t.fromQty || 0) && (t.toQty == null || q <= t.toQty));
  if (!hit) hit = [...sorted].reverse().find(({ t }) => q >= (t.fromQty || 0));
  if (!hit) hit = sorted[0];
  return hit ? { tier: hit.t, index: hit.index } : { tier: null, index: -1 };
}

/** 국가별 기본 조건 + 시뮬레이션 상태 (제품마다 독립). */
export type CountrySim = {
  id: string; // 'IN' | 'TW' | 'VN' | 자유 추가
  name: string;
  costKrw: number; // 제조원가 KRW/대
  fx: number; // USD→KRW
  floorCompany: number; // 회사 플로어(USD) = 인센티브 기준선
  floorRef: number; // 참고 플로어 (수식 미사용)
  rate: number; // 기본 수수료율 (0~1)
  tiers: Tier[];
  /** 레거시(v2) — offers로 이관됨. 저장분 마이그레이션용으로만 유지. */
  scenarios?: Scenario[];
  offers: Offer[];
};

export type SaleRow = {
  id: string;
  date: string; // YYYY-MM-DD
  country: string;
  buyer: string;
  qty: number;
  priceUsd: number;
  fx: number;
  costKrw: number; // 제조원가 KRW/대 (당시값)
  commKrw: number; // 수수료 총액 KRW (실지급)
  extraKrw: number; // 운임·보험·기타 비용 총액 KRW
  /** 이 출고가 귀속되는 계약 (달성률 집계 기준). null = 미연결 */
  contractId?: string | null;
  note: string;
};

/** 계약 — 확정된 MOQ 쿼터 조건 + 계약 후 단계(데모 선반입→인허가→쿼터) 타임라인. */
export type Contract = {
  id: string;
  name: string; // 예: "인도 · Primelaze 2026 연간"
  country: string;
  buyer: string;
  startDate: string; // 계약일 YYYY-MM-DD (옵션, 빈 문자열 허용)
  endDate: string; // 쿼터 종료일
  /** 계약 후 단계 — 데모장비 선반입 기간·물량 */
  demoStart: string;
  demoEnd: string;
  demoQty: number;
  /** 인허가 단계 (예: CDSCO MD-15) */
  licenseLabel: string;
  licenseStart: string;
  licenseEnd: string;
  licenseDone: boolean;
  /** MOQ 쿼터 기산일 — 계약 시점과 무관하게 설정 가능 (통상 인허가 완료 후). 비우면 startDate */
  quotaStart: string;
  tiers: OfferTier[]; // 계약된 MOQ 구간표 (쿼터 구간)
  free: FreeRule; // 무상 제공 조건 (예: 누적 30대당 1대)
  targetQty: number | null; // 쿼터 목표 수량. 비우면 마지막 구간 상한 사용
  note: string;
};

const DAY = 86_400_000;
const d2t = (s: string) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(s + "T00:00:00").getTime() : null);

/**
 * 달성 전제 페이스 진단 — 쿼터 기간(quotaStart~endDate)에 목표를 균등 배분한
 * "목표 누적"과 실적을 비교하고, 현재 페이스 유지 시 예상 최종치를 전망한다.
 */
export function contractPace(ct: Contract, sold: number, target: number | null, today: string) {
  const qs = d2t(ct.quotaStart || ct.startDate);
  const qe = d2t(ct.endDate);
  const now = d2t(today);
  if (qs == null || qe == null || now == null || qe <= qs || target == null || target <= 0) return null;
  const total = qe - qs;
  const elapsed = Math.min(Math.max(now - qs, 0), total);
  const elapsedPct = elapsed / total;
  const expectedQty = target * elapsedPct; // 달성 전제 오늘까지 목표 누적
  const paceDiff = sold - expectedQty; // +면 목표 페이스보다 앞섬
  const projectedEnd = elapsed > 0 ? (sold / elapsed) * total : null; // 현재 페이스 유지 시 최종
  const remainDays = Math.max(0, Math.round((qe - now) / DAY));
  const remainQty = Math.max(0, target - sold);
  const requiredMonthly = remainDays > 0 ? remainQty / (remainDays / 30.44) : null; // 달성 전제 필요 월 페이스
  return {
    quotaStarted: now >= qs,
    elapsedPct,
    expectedQty,
    paceDiff,
    projectedEnd,
    remainDays,
    requiredMonthly,
  };
}

export type ProductState = {
  countries: CountrySim[];
  sales: SaleRow[];
  contracts: Contract[];
};

/** 계약 달성 집계 — 연결된 판매 기록의 유상 수량 누적 vs 쿼터 목표. */
export function contractProgress(ct: Contract, sales: SaleRow[]) {
  const rows = sales.filter((s) => s.contractId === ct.id);
  const sold = rows.reduce((s, r) => s + (r.qty || 0), 0);
  const revenueKrw = rows.reduce((s, r) => s + (r.qty || 0) * (r.priceUsd || 0) * (r.fx || 0), 0);
  const sorted = [...ct.tiers].sort((a, b) => (a.fromQty || 0) - (b.fromQty || 0));
  const lastTo = sorted.reduce<number | null>((acc, t) => (t.toQty != null ? Math.max(acc ?? 0, t.toQty) : acc), null);
  const target = ct.targetQty ?? lastTo;
  const { tier: cur } = applicableTier(sorted, Math.max(sold, 1));
  const next = sorted.find((t) => (t.fromQty || 0) > sold);
  const free = ct.free ?? emptyFree();
  return {
    sold,
    revenueKrw,
    rowCount: rows.length,
    target,
    ratio: target && target > 0 ? sold / target : null,
    currentPrice: sold > 0 ? (cur?.price ?? null) : null,
    nextTierAt: next ? next.fromQty : null,
    remainToTarget: target != null ? Math.max(0, target - sold) : null,
    /** 무상 발생 대수 (누적 규칙 + 고정) */
    freeEarned: freeEarned(free, sold),
    /** 다음 무상 발생까지 남은 유상 대수 */
    nextFreeIn: free.freePerN && free.freePerN > 0 ? free.freePerN - (sold % free.freePerN) : null,
  };
}

export type DeskState = {
  version: 4;
  products: Record<ProductId, ProductState>;
};

export function makeOffer(
  label: string,
  tiers: OfferTier[] = [{ fromQty: 1, toQty: null, price: 0, freeUnits: 0 }],
  over: Partial<Offer> = {},
): Offer {
  return { label, tiers, orderQty: 10, free: emptyFree(), costs: [], rateOverride: null, note: "", ...over };
}
export const tier = (fromQty: number, toQty: number | null, price: number, freeUnits = 0): OfferTier => ({
  fromQty,
  toQty,
  price,
  freeUnits,
});

/** 기준안 — 가격 구간 레퍼런스와 실시간 연동되는 첫 번째 안. */
export function makeLinkedOffer(orderQty = 50): Offer {
  return {
    label: "기준안 · 레퍼런스 연동",
    linked: true,
    tiers: [],
    orderQty,
    free: emptyFree(),
    costs: [],
    rateOverride: null,
    note: "",
  };
}

// ── 기본값 ────────────────────────────────────────────────────────────────
const TORR_TIERS: Tier[] = [
  { label: "up to 20대", price: 14_000, fromQty: 1, toQty: 20 },
  { label: "21–49대", price: 12_000, fromQty: 21, toQty: 49 },
  { label: "50–100대", price: 10_000, fromQty: 50, toQty: 100 },
  { label: "데모장비", price: 8_000 }, // 범위 없음 → 기준안 연동에서 제외
];
const TORR_OFFERS: Offer[] = [
  makeLinkedOffer(50),
  makeOffer("A안 · 대량 단일가 + 30대당 무상 1대", [tier(1, null, 10_000)], {
    orderQty: 100,
    free: { freeFixed: 0, freePerN: 30, freePerUnits: 1 },
    costs: [{ name: "운임 지원", base: "fixed_krw", value: 0 }],
  }),
];

function country(id: string, name: string, over: Partial<CountrySim> = {}): CountrySim {
  return {
    id,
    name,
    costKrw: 0,
    fx: 1_500,
    floorCompany: 0,
    floorRef: 0,
    rate: 0,
    tiers: [{ label: "기본", price: 0, fromQty: 1, toQty: null }],
    offers: [makeLinkedOffer(10), makeOffer("A안")],
    ...over,
  };
}

/** TORR RF 인도 = v1.1 원본값. 그 외 국가/제품은 조건 미확정(0) — 입력 후 저장하면 기본조건이 된다. */
export function defaultState(): DeskState {
  const torrBase = () => ({
    costKrw: 5_000_000,
    floorCompany: 12_000,
    floorRef: 13_000,
    rate: 0.05,
    tiers: TORR_TIERS.map((t) => ({ ...t })),
    offers: TORR_OFFERS.map((o) => ({ ...o })),
  });
  return {
    version: 4,
    products: {
      "torr-rf": {
        countries: [country("IN", "인도", torrBase()), country("TW", "대만", torrBase()), country("VN", "베트남", torrBase())],
        sales: [],
        contracts: [],
      },
      newchae: { countries: [country("TW", "대만"), country("IN", "인도"), country("VN", "베트남")], sales: [], contracts: [] },
      "luminowave-kc": { countries: [country("TW", "대만"), country("IN", "인도"), country("VN", "베트남")], sales: [], contracts: [] },
      "luminowave-md": { countries: [country("TW", "대만"), country("IN", "인도"), country("VN", "베트남")], sales: [], contracts: [] },
    },
  };
}

// ── 계산 (v1.1 일반화) ─────────────────────────────────────────────────────
export function commissionUsd(price: number, c: Pick<CountrySim, "rate" | "floorCompany">): number {
  return price * c.rate + Math.max(0, price - c.floorCompany);
}

export function tierCalc(t: Tier, c: CountrySim) {
  const priceKrw = t.price * c.fx;
  const marginKrw = priceKrw - c.costKrw;
  const base = t.price * c.rate;
  const incentive = Math.max(0, t.price - c.floorCompany);
  const commUsd = base + incentive;
  return {
    priceKrw,
    marginKrw,
    marginPct: priceKrw > 0 ? marginKrw / priceKrw : NaN,
    base,
    incentive,
    commUsd,
    commKrw: commUsd * c.fx,
    netKrw: (t.price - commUsd) * c.fx - c.costKrw,
  };
}

/**
 * 가격안(딜 패키지) 총액 계산 — 주문 수량이 걸린 MOQ 구간의 단가가 전량 적용(실무 MOQ 방식).
 * 매출/지출(원가·수수료·명목별) 분해 + 순이익.
 */
export function offerCalc(o: Offer, c: CountrySim) {
  const rate = o.rateOverride ?? c.rate;
  const { tier: t } = applicableTier(effectiveTiers(o, c), o.orderQty);
  const units = o.orderQty || 0;
  const price = t?.price ?? 0;
  const freeUnits = freeEarned(o.free ?? emptyFree(), units);
  const revenueUsd = units * price;
  // 수수료 = 판매수수료(기본율, 총판매액 기준) + 차액 인센티브(플로어 초과분) — 분리 집계
  const commBaseUsd = units * price * rate;
  const commIncentiveUsd = units * Math.max(0, price - c.floorCompany);
  const commUsd = commBaseUsd + commIncentiveUsd;
  const revenueKrw = revenueUsd * c.fx;
  const costMfg = (units + freeUnits) * c.costKrw; // 무상분도 원가는 발생
  const costComm = commUsd * c.fx;
  const costCommBase = commBaseUsd * c.fx;
  const costCommIncentive = commIncentiveUsd * c.fx;
  const grossMargin = revenueKrw - costMfg; // 총마진 (pct_margin 기준값)
  const customCosts = o.costs.map((it) => {
    let amountKrw = 0;
    if (it.base === "fixed_krw") amountKrw = it.value || 0;
    else if (it.base === "per_unit_krw") amountKrw = units * (it.value || 0);
    else if (it.base === "pct_revenue") amountKrw = revenueKrw * ((it.value || 0) / 100);
    else if (it.base === "pct_margin") amountKrw = grossMargin * ((it.value || 0) / 100);
    return { name: it.name, base: it.base, value: it.value, amountKrw };
  });
  const costCustom = customCosts.reduce((s, x) => s + x.amountKrw, 0);
  const costTotal = costMfg + costComm + costCustom;
  const netKrw = revenueKrw - costTotal;
  return {
    rate,
    units,
    freeUnits,
    avgPriceUsd: units > 0 ? revenueUsd / units : NaN,
    revenueUsd,
    revenueKrw,
    costMfg,
    costComm,
    costCommBase,
    costCommIncentive,
    customCosts,
    costCustom,
    costTotal,
    grossMargin,
    netKrw,
    netPerUnitKrw: units > 0 ? netKrw / units : NaN,
    marginPct: revenueKrw > 0 ? netKrw / revenueKrw : NaN,
  };
}

export function saleCalc(r: SaleRow) {
  const revenueKrw = r.qty * r.priceUsd * r.fx;
  const costTotal = r.qty * r.costKrw + r.commKrw + r.extraKrw;
  const marginKrw = revenueKrw - costTotal;
  return {
    revenueUsd: r.qty * r.priceUsd,
    revenueKrw,
    costTotal,
    marginKrw,
    marginPct: revenueKrw > 0 ? marginKrw / revenueKrw : NaN,
  };
}

// ── 포맷터 ────────────────────────────────────────────────────────────────
export const krw = (n: number) => (Number.isFinite(n) ? Math.round(n).toLocaleString("ko-KR") : "—");
export const usd = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—";
export const pct = (n: number) => (Number.isFinite(n) ? (n * 100).toFixed(1) + "%" : "—");

// ── 영속화 (Supabase app_state 우선, localStorage 캐시) ────────────────────
const KEY = "margin-desk";
const LS = "marginDeskV2";

export function migrate(raw: unknown): DeskState {
  const base = defaultState();
  if (!raw || typeof raw !== "object") return base;
  const s = raw as Partial<DeskState>;
  if (!s.products) return base;
  for (const p of PRODUCTS) {
    const ps = (s.products as Record<string, ProductState | undefined>)[p.id];
    if (!ps || !Array.isArray(ps.countries) || ps.countries.length === 0) {
      (s.products as Record<string, ProductState>)[p.id] = base.products[p.id];
      continue;
    }
    if (!Array.isArray(ps.sales)) ps.sales = [];
    if (!Array.isArray(ps.contracts)) ps.contracts = [];
    for (const ct of ps.contracts) {
      if (!ct.free) ct.free = { ...emptyFree(), freeFixed: ct.tiers?.reduce((s2, t) => s2 + (t.freeUnits || 0), 0) || 0 };
      if (typeof ct.demoStart !== "string") ct.demoStart = "";
      if (typeof ct.demoEnd !== "string") ct.demoEnd = "";
      if (typeof ct.demoQty !== "number") ct.demoQty = 0;
      if (typeof ct.licenseLabel !== "string") ct.licenseLabel = "CDSCO 인허가";
      if (typeof ct.licenseStart !== "string") ct.licenseStart = "";
      if (typeof ct.licenseEnd !== "string") ct.licenseEnd = "";
      if (typeof ct.licenseDone !== "boolean") ct.licenseDone = false;
      if (typeof ct.quotaStart !== "string") ct.quotaStart = "";
    }
    // v2 scenarios / v3 단일가 / v4 수량계단 → v5 구간표(범위) 딜로 승격.
    for (const c of ps.countries) {
      type LegacyTier = Partial<OfferTier> & { qty?: number };
      type LegacyOffer = Partial<Omit<Offer, "tiers">> & {
        tiers?: LegacyTier[];
        price?: number;
        qty?: number;
        freeUnits?: number;
        extraKrw?: number;
        label?: string;
      };
      const legacyOffers = (Array.isArray(c.offers) ? c.offers : []) as LegacyOffer[];
      if (legacyOffers.length === 0) {
        const scn = Array.isArray(c.scenarios) ? c.scenarios : [];
        c.offers = scn.length
          ? scn.map((sc, i) => makeOffer(`${String.fromCharCode(65 + i)}안`, [tier(1, null, sc.price)], { orderQty: sc.qty }))
          : [makeOffer("A안"), makeOffer("B안")];
      } else {
        c.offers = legacyOffers.map((o, i) => {
          const label = o.label ?? `${String.fromCharCode(65 + i)}안`;
          const common = {
            rateOverride: o.rateOverride ?? null,
            note: o.note ?? "",
            costs: Array.isArray(o.costs)
              ? o.costs
              : o.extraKrw
                ? [{ name: "운임·기타", base: "fixed_krw" as const, value: o.extraKrw }]
                : [],
          };
          if (Array.isArray(o.tiers) && o.tiers.length) {
            const first = o.tiers[0]!;
            if (typeof first.fromQty === "number") {
              // v5/v6 형태 — 필드 보정 + 구간의 무상을 딜 레벨 고정 무상으로 이관
              const tierFree = o.tiers.reduce((s2, t) => s2 + (t.freeUnits || 0), 0);
              const freeBase = (o as { free?: FreeRule }).free ?? emptyFree();
              return makeOffer(label, o.tiers.map((t) => tier(t.fromQty || 1, t.toQty ?? null, t.price || 0)), {
                ...common,
                orderQty: typeof o.orderQty === "number" ? o.orderQty : 10,
                free: { ...emptyFree(), ...freeBase, freeFixed: (freeBase.freeFixed || 0) + tierFree },
              });
            }
            // v4 수량 계단(qty별 누적 구매) → 누적 경계로 범위 변환 + 주문수량 = 총합
            let acc = 0;
            const tierFree2 = o.tiers.reduce((s2, t) => s2 + (t.freeUnits || 0), 0);
            const ranges = o.tiers.map((t) => {
              const from = acc + 1;
              acc += t.qty || 0;
              return tier(from, acc, t.price || 0);
            });
            return makeOffer(label, ranges, { ...common, orderQty: acc || 10, free: { ...emptyFree(), freeFixed: tierFree2 } });
          }
          // v3 단일가
          return makeOffer(label, [tier(1, null, o.price || 0)], {
            ...common,
            orderQty: o.qty || 10,
            free: { ...emptyFree(), freeFixed: o.freeUnits || 0 },
          });
        });
      }
      delete c.scenarios;
      // 레퍼런스 구간에 범위 백필 (라벨에서 추정: "21–49대", "up to 20대" 등) — 이미 있으면 유지.
      if (Array.isArray(c.tiers)) {
        for (const t of c.tiers) {
          if (typeof t.fromQty === "number") continue;
          const range = String(t.label ?? "").match(/(\d+)\s*[–~-]\s*(\d+)/);
          const upTo = String(t.label ?? "").match(/up\s*to\s*(\d+)/i);
          if (range) {
            t.fromQty = Number(range[1]);
            t.toQty = Number(range[2]);
          } else if (upTo) {
            t.fromQty = 1;
            t.toQty = Number(upTo[1]);
          }
        }
      }
      // 기준안(레퍼런스 연동)이 없으면 맨 앞에 삽입 — 비교안은 두 번째부터 A/B/C.
      if (!c.offers.some((o) => o.linked)) {
        const firstQty = c.offers[0]?.orderQty ?? 50;
        c.offers = [makeLinkedOffer(firstQty), ...c.offers];
      }
    }
  }
  return { version: 4, products: s.products as DeskState["products"] };
}

export async function loadState(): Promise<{ state: DeskState; source: "server" | "local" | "default" }> {
  try {
    const row = await appStateRepo.get(KEY);
    if (row) return { state: migrate(row.data), source: "server" };
    // 서버에 아직 없음 → 로컬 캐시가 있으면 승계
    const ls = localStorage.getItem(LS);
    if (ls) return { state: migrate(JSON.parse(ls)), source: "local" };
    return { state: defaultState(), source: "default" };
  } catch {
    /* offline/미설정 → local */
  }
  try {
    const ls = localStorage.getItem(LS);
    if (ls) return { state: migrate(JSON.parse(ls)), source: "local" };
  } catch {
    /* corrupt */
  }
  return { state: defaultState(), source: "default" };
}

export async function saveState(state: DeskState): Promise<boolean> {
  localStorage.setItem(LS, JSON.stringify(state));
  try {
    await appStateRepo.put(KEY, state);
    return true;
  } catch {
    return false;
  }
}
